import type { ExpirationCondition, RecurringDamage } from '../domain/encounter.ts';
import type { Effect, Modifier } from '../rules/effects.ts';
import type { SourceRef } from './types.ts';

/**
 * Buffs and Debuffs as content.
 *
 * A condition is nothing but an `Effect` plus the printed ways it ends. It uses
 * the same resolver, the same damage pipeline and the same roll services as
 * everything else; there is no condition rules engine.
 */
export interface ConditionDefinition {
  id: string;
  name: string;
  kind: 'buff' | 'debuff';
  /** Rules p.45: some Debuffs stack. Two instances then both apply. */
  stackable: boolean;
  summary: string;
  modifiers: Modifier[];
  /** Short mechanical labels the resolver cannot express as a modifier. */
  notes: string[];
  /** Printed effects this application deliberately does NOT model yet. */
  unmodelled: string[];
  recurringDamage?: RecurringDamage;
  endsWhen: ExpirationCondition[];
  /**
   * Rules p.45: "Gaining a Major Injury a second time changes it to a
   * Long-Term Major Injury." A narrow structured mechanic for the only two
   * conditions that print it, not a general rule.
   */
  escalatesTo?: string;
  source: SourceRef;
}

export interface FatalDamageSave {
  description: string;
  statCheck: string;
  difficulty: 'ten-plus-floor';
  requiresAction: boolean;
  source: SourceRef;
}

export interface ConditionContent {
  /**
   * RULE DCC-COND-001 - Rules for Survival p.46: "Damage from Debuffs bypasses
   * DR, but other resistances and immunities apply as normal."
   */
  damageBypassesDamageResistance: boolean;
  damageRuleSource: SourceRef;
  /**
   * RULE DCC-COND-007 - Rules p.44: "If a Debuff applies damage to a crawler
   * that would drop them to 0% on their Health Bar, they can make a Con Stat
   * Check vs. Difficulty 10 + Floor during Step 5: Clean Up... On a Success,
   * the Debuff ends, and the crawler avoids the damage."
   */
  fatalDamageSave: FatalDamageSave;
  conditions: ConditionDefinition[];
}

export function getCondition(
  content: ConditionContent,
  id: string,
): ConditionDefinition | undefined {
  return content.conditions.find((c) => c.id === id);
}

export function allConditions(content: ConditionContent): readonly ConditionDefinition[] {
  return content.conditions;
}

/**
 * Turns a definition into a live `Effect`.
 *
 * `instanceId` distinguishes two stacks of the same Debuff. `sourceId` stays
 * the definition's ID, so provenance in the roll history names the Debuff
 * rather than the stack.
 */
export function instantiateCondition(
  definition: ConditionDefinition,
  instanceId: string,
): Effect {
  return {
    id: instanceId,
    sourceId: definition.id,
    sourceKind: definition.kind,
    label: definition.name,
    modifiers: definition.modifiers.map((m) => ({ ...m })),
  };
}
